import { useClipboard } from '@vueuse/core'
import { onUnmounted, ref } from 'vue'

import { del, get, put } from '@/api'
import { pushStickyToast, pushToast } from '@/utils/toasts'

const POLL_INTERVAL_MS = 5000

/**
 * Drives the async RO-Crate export flow for a WorkflowVersion or WorkflowRun.
 *
 * The /export/ endpoint splits by HTTP method:
 *   - GET reports current state and never enqueues:
 *       WV: { status: 'ready', download_url, attachment_id, file_size, source }
 *       WR: { status: 'ready', path, source, generated_at, generated_by, export_type, file_size }
 *           OR { status: 'ready', path, source: 'imported', export_type }
 *       Both: { status: 'in_progress', user_task_id } | { status: 'idle' }
 *   - PUT explicitly triggers a fresh generation:
 *       { status: 'queued', user_task_id, task_id }
 *       WR only: accepts optional ?path=<custom_path> query param.
 *
 * Once a task is queued we poll GET /export/ directly and stop as soon as it
 * reports 'ready'. Only PUT triggers generation, so GET is safe to hit on a
 * tick. Failures are surfaced by an email to the user, not by the UI.
 */
export function useRocrateExport({ appName, modelName, recordId }) {
  const status = ref('idle') // 'idle' | 'in_progress' | 'ready'
  // WV: URL to download the crate attachment directly in-browser.
  const downloadUrl = ref(null)
  // WV: ID of the attachment record (used for delete).
  const attachmentId = ref(null)
  // WV: original filename of the crate zip.
  const filename = ref(null)
  // WV/WR: whether the crate is the original imported one (undeletable).
  const isPrimaryRocrate = ref(false)
  // WR: absolute filesystem path the user copies and retrieves themselves.
  const path = ref(null)
  // 'imported' | 'generated' | null
  const source = ref(null)
  // Legacy alias — callers that used dropboxPath still work via this ref.
  const dropboxPath = path
  const generatedAt = ref(null)
  const generatedBy = ref(null)
  const exportType = ref(null)
  const fileSize = ref(null)
  const isLaunching = ref(false)
  // WR: server-computed hints for the generate dialog. `customPathAllowed`
  // gates whether the input is offered at all (users with a configured
  // dropbox cannot supply a custom path); `allowedRoots` is the admin-set
  // whitelist of directory prefixes the value must sit under.
  const customPathAllowed = ref(false)
  const allowedRoots = ref([])

  const { copy } = useClipboard()
  let pollTimeoutId = null

  const stopPolling = () => {
    if (pollTimeoutId !== null) {
      clearTimeout(pollTimeoutId)
      pollTimeoutId = null
    }
  }

  const resolveRecordId = () =>
    typeof recordId === 'object' ? recordId?.value : recordId

  const buildUrl = ({ root = null, subpath = null } = {}) => {
    const base = `${appName}/${modelName}/${resolveRecordId()}/export/?type=full`
    const params = []
    if (root) params.push(`root=${encodeURIComponent(root)}`)
    if (subpath) params.push(`subpath=${encodeURIComponent(subpath)}`)
    return params.length ? `${base}&${params.join('&')}` : base
  }

  const applyResponse = (data) => {
    if (!data || !data.status) return
    if ('custom_path_allowed' in data) {
      customPathAllowed.value = data.custom_path_allowed === true
    }
    if (Array.isArray(data.allowed_roots)) {
      allowedRoots.value = data.allowed_roots
    }
    if (data.status === 'ready') {
      status.value = 'ready'
      // WV shape: download_url present
      downloadUrl.value = data.download_url || null
      attachmentId.value = data.attachment_id || null
      filename.value = data.filename || null
      isPrimaryRocrate.value = data.is_primary_rocrate ?? false
      // WR shape: path present
      path.value = data.path || null
      source.value = data.source || null
      generatedAt.value = data.generated_at || null
      generatedBy.value = data.generated_by || null
      exportType.value = data.export_type || null
      fileSize.value = data.file_size ?? null
      stopPolling()
    } else if (data.status === 'in_progress' || data.status === 'queued') {
      status.value = 'in_progress'
      if (pollTimeoutId === null) schedulePoll()
    } else if (data.status === 'idle') {
      status.value = 'idle'
      downloadUrl.value = null
      attachmentId.value = null
      filename.value = null
      isPrimaryRocrate.value = false
      path.value = null
      source.value = null
      generatedAt.value = null
      generatedBy.value = null
      fileSize.value = null
      stopPolling()
    }
  }

  const fetchStatus = async () => {
    if (!resolveRecordId()) return null // record not loaded yet — nothing to fetch
    try {
      const response = await get(buildUrl(), { polling: true })
      applyResponse(response?.data)
      return response?.data
    } catch {
      // Silent on initial mount — 400 (not committed) etc. are legitimate for
      // detail pages that only want to know whether a delivery already exists.
      return null
    }
  }

  const schedulePoll = () => {
    stopPolling()
    pollTimeoutId = setTimeout(async () => {
      if (status.value !== 'in_progress') return
      await fetchStatus()
      if (status.value === 'in_progress') schedulePoll()
    }, POLL_INTERVAL_MS)
  }

  /**
   * Trigger a fresh generation PUT. For WorkflowRun, optionally pass a
   * `{ root, subpath }` pair to override the default dropbox target — the
   * server picks the file inside `root/subpath` and creates the subpath
   * directory tree if it does not yet exist.
   */
  const requestGeneration = async ({ root = null, subpath = null } = {}) => {
    if (status.value === 'in_progress') {
      pushToast({
        type: 'info',
        message: 'RO-Crate is being generated',
        description: "You'll get an email once it's ready.",
      })
      return
    }

    if (!resolveRecordId()) return

    isLaunching.value = true
    try {
      const response = await put(buildUrl({ root, subpath }))
      applyResponse(response?.data)

      if (status.value === 'in_progress') {
        pushToast({
          type: 'success',
          message: 'RO-Crate generation launched',
          description:
            "You'll get an email when it's ready. You can leave this page.",
        })
      }
    } catch (err) {
      // Server-side preflight failures: 400 {"validation_errors": [...]}.
      const currentRecordId = resolveRecordId()
      const validationErrors = err?.response?.data?.validation_errors
      if (Array.isArray(validationErrors) && validationErrors.length > 0) {
        validationErrors.forEach((msg) => {
          pushStickyToast({
            type: 'error',
            message: 'RO-Crate cannot be generated yet',
            description: msg,
            groupKey: `rocrate-validation-${currentRecordId}`,
          })
        })
        return
      }
      pushToast({
        type: 'error',
        message: 'Could not launch RO-Crate generation',
        description: err?.response?.data?.message || err?.message,
        groupKey: `rocrate-launch-${currentRecordId}`,
      })
    } finally {
      isLaunching.value = false
    }
  }

  const copyPath = async () => {
    const toCopy = path.value
    if (!toCopy) return
    await copy(toCopy)
    pushToast({
      type: 'success',
      message: 'Copied path',
      description: toCopy,
    })
  }

  // Legacy alias
  const copyDropboxPath = copyPath

  const deleteRocrate = async () => {
    if (!attachmentId.value) return
    try {
      await del(`data_management/attachments/${attachmentId.value}/`)
      applyResponse({ status: 'idle' })
      pushToast({ type: 'success', message: 'RO-Crate deleted' })
    } catch (err) {
      pushToast({
        type: 'error',
        message: 'Failed to delete RO-Crate',
        description: err?.response?.data?.message || err?.message,
      })
    }
  }

  onUnmounted(stopPolling)

  return {
    status,
    downloadUrl,
    attachmentId,
    filename,
    isPrimaryRocrate,
    path,
    source,
    dropboxPath,
    generatedAt,
    generatedBy,
    exportType,
    fileSize,
    isLaunching,
    customPathAllowed,
    allowedRoots,
    fetchStatus,
    requestGeneration,
    copyPath,
    copyDropboxPath,
    deleteRocrate,
  }
}
